import { z } from 'zod';
/** E.164 phone number, e.g. +33970550134. */
export const E164 = z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'must be E.164, e.g. +33970550134');
/** IANA timezone identifier, e.g. Europe/Paris. Validated against the runtime's ICU db. */
export const Timezone = z.string().refine((tz) => {
    try {
        // Throws RangeError for an unknown zone.
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'must be a valid IANA timezone' });
/** YYYY-MM-DD date in the business timezone (no time component). */
export const LocalDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD')
    /**
     * ...and a date that exists. The shape check alone accepted `2026-09-31`, and
     * the model does ask for dates like that — it said "the thirty-first of
     * September" out loud on a real call (doc 30 §4). Downstream that became an
     * "Invalid DateTime" handed back as the weekday to read to the caller.
     * Rejecting it here turns a nonsense date into `invalid_arguments`, which the
     * model is already instructed to retry with corrected arguments.
     */
    .refine((value) => {
    // Fixed offsets, not a split: the regex above has already guaranteed the
    // shape, and destructuring `split('-')` yields `number | undefined` under
    // noUncheckedIndexedAccess.
    const y = Number(value.slice(0, 4));
    const m = Number(value.slice(5, 7));
    const d = Number(value.slice(8, 10));
    const utc = new Date(Date.UTC(y, m - 1, d));
    return utc.getUTCFullYear() === y && utc.getUTCMonth() === m - 1 && utc.getUTCDate() === d;
}, 'must be a real calendar date');
/**
 * A timestamp accepted at any trust boundary and normalised to a JS `Date`.
 *
 * The shared package cannot depend on Firebase (ADR-0008 rule 3), yet the same
 * schema validates documents read via the Admin SDK (server), the Web SDK
 * (browser), emulator JSON, and plain ISO strings. This coercer accepts every
 * shape a Firestore `Timestamp` can arrive as and yields a `Date`.
 */
export const TimestampSchema = z.preprocess((value) => {
    if (value instanceof Date)
        return value;
    if (typeof value === 'string' || typeof value === 'number') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? undefined : d;
    }
    if (value && typeof value === 'object') {
        const obj = value;
        // Firestore Timestamp (both SDKs) exposes toDate().
        if (typeof obj.toDate === 'function')
            return obj.toDate();
        // Serialized Timestamp: { seconds, nanoseconds } or { _seconds, _nanoseconds }.
        const seconds = obj.seconds ?? obj._seconds;
        const nanos = obj.nanoseconds ?? obj._nanoseconds ?? 0;
        if (typeof seconds === 'number' && typeof nanos === 'number') {
            return new Date(seconds * 1000 + Math.floor(nanos / 1e6));
        }
    }
    return undefined;
}, z.date());
/** Minutes from local midnight, 0..1440. Used by the availability grid. */
export const MinuteOfDay = z.number().int().min(0).max(1440);
/** A single open interval within a day, in minutes from local midnight. */
export const TimeInterval = z
    .object({
    startMin: MinuteOfDay,
    endMin: MinuteOfDay,
})
    .refine((i) => i.endMin > i.startMin, {
    message: 'endMin must be after startMin',
});
//# sourceMappingURL=primitives.js.map